<script lang="ts">
    import type {Employee} from "$lib/types/employee";
    import {Button, Card, Input, Label, Modal, Select, Spinner, Textarea, Tooltip} from "flowbite-svelte";
    import {
        ArchiveSolid,
        BellActiveAltSolid,
        CalendarMonthSolid,
        ClipboardCheckSolid,
        MobilePhoneSolid
    } from "flowbite-svelte-icons";
    import {get, post, put} from "$lib/api";
    import {onMount} from "svelte";
    import {getUnits} from "$lib/calls/unit";
    import {invalidateAll} from "$app/navigation";
    import {PUBLIC_RECRUITING_APP} from "$env/static/public";
    import dayjs from "dayjs";

    export let employee: Employee;

    let form: HTMLFormElement;

    let templateId: number = 0;

    let applicableMailjetTemplate: any;

    let employeeUnit: {id:number, address: {name: string, street: string, number: string, zip: string, place: string} | null} = {id:1,address:null};

    const mailjetTemplates = {
        // TODO: abstract trigger mails
        requestPhoneInterview: () => ({
            id: 6738391,
            variables: {
                Year: dayjs().year(),
                unit_phoneNumber: employeeUnit?.phoneNumber,
                firstName: employee.firstName, url: PUBLIC_RECRUITING_APP +'/schedule?sheet=' + employee.uuid,
                unsubscribe: 'https://api.demo.celest.rest/unsubscribe/employee/' + employee.uuid
            },
            subject: 'Wir möchten dich kennenlernen!',
        }),
        requestBaseData: ()=> ({
            id: 6753694,
            variables: {Year: dayjs().year(), firstName: employee.firstName, url: PUBLIC_RECRUITING_APP + '/data-request?sheet=' + employee.uuid, unsubscribe: 'https://api.celest.services/unsubscribe/employee/' + employee.uuid},
            subject: 'Wir brauchen noch deine Stammdaten!',
        }),
        requestGetToKnowAppointment: () => ({
            id: 6753645,
            variables: {
                Year: dayjs().year(),
                firstName: employee.firstName,
                url: PUBLIC_RECRUITING_APP +  '/schedule?sheet=' + employee.uuid,
                addressName: employeeUnit.address?.name || 'REcrew-Turm',
                addressLine1: employeeUnit.address?.street + ' ' + employeeUnit.address?.number,
                addressLine2: employeeUnit.address?.zip + ' ' + employeeUnit.address?.place,
                unsubscribe: 'https://api.demo.celest.rest/unsubscribe/employee/' + employee.uuid
            },
            subject: 'Einladung Get-to-Know',
        })
    }
    let html = '';
    let previewModal = false;
    let subject = '';
    let templates:any[] = [];
    let poolModal = false;
    let poolReason = '';

    function requestTemplate(key: keyof typeof mailjetTemplates) {
        html = '';
        previewModal = true;
        applicableMailjetTemplate = mailjetTemplates[key]();
        subject = applicableMailjetTemplate.subject;
        const template = templates.find(template => template.ID === applicableMailjetTemplate.id) || templates[0];
        templateId = template.ID;
        previewEmail();
    }
    function previewEmail() {
        get('/mail-template/' + templateId).then(res => {
            html = res[0]['Html-part'];

            Object.entries(applicableMailjetTemplate.variables).forEach((variable) => {
                const reg = new RegExp(`{{var:${variable[0]}(:"[\\w\\s-]*")*}}`, 'g');
                html = html.replace(reg, variable[1]);
            })
        })
    }
    async function submitForm() {
        try{
            const mail = await post('/mail-template/send/' + templateId, {
                fromEmail: 'no-reply@celest.rest',
                fromName: 'XY REcrew\'ting',
                subject: subject,
                variables: applicableMailjetTemplate.variables,
                toEmail: employee.email,
                toName: employee.name,
            })
            previewModal = false;
            form.submit();
        } catch (e) {
            alert('Unbekannter Fehler mit Template')
        }
    }
    async function moveToPool() {
        await post('/employee/' + employee.uuid + '/timeline', {
            content: "Bewerbung in den Pool verschoben. Grund: \n" + poolReason,
            type: 'important'
        });
        poolModal = false;
        const {employmentStatus} = await put('/employee/' + employee.uuid, {
            employmentStatus: 'pool'
        })
        employee.employmentStatus = employmentStatus
    }
    async function syncEmployee(){
        await get('/hr/sync-employee/' + employee.uuid)
        await invalidateAll();
    }

    onMount(async() => {
        employeeUnit = (await getUnits()).find((unit:any) => unit.id === employee.unitId);
        console.log({employeeUnit})
        templates = await get('/mail-template');
    })


</script>

    <form class="flex gap-3" bind:this={form} action="?/update_employee_status" method="post">
        {#if employee.employmentStatus !== 'active' && employee.employmentStatus !== 'pool' && employee.employmentStatus !== 'onboarding' && employee.employmentStatus !== 'rebuddy'}
            <Button on:click={() => poolModal = true} size="xs" color="alternative" class="text-neutral-600">
                <ArchiveSolid class="w-4 mr-2"/>
                Pool
            </Button>
        {/if}
        {#if employee.employmentStatus === 'data-provided'}
            <input type="hidden" name="status" value="data-verified">
            <Button on:click={() => requestTemplate('requestGetToKnowAppointment')} type="button" class="bg-sky-100 text-neutral-600" size="xs">
                <CalendarMonthSolid class="w-4 mr-2"/>
                Stammdaten akzeptieren
            </Button>
            <Tooltip>Stammdaten als komplett markieren & Schulungstermin vereinbaren</Tooltip>
        {:else if employee.employmentStatus === 'telephone-interview'}
            <input type="hidden" name="status" value="data-requested">
            <Button on:click={() => requestTemplate('requestBaseData')} type="button" class="bg-sky-100 text-neutral-600" size="xs">
                <MobilePhoneSolid class="w-4 mr-2"/>
                Stammdaten anfordern
            </Button>
        {:else if employee.employmentStatus === 'candidate'}
            <input type="hidden" name="status" value="interviewee">
            <Button on:click={() => requestTemplate('requestPhoneInterview')} type="button"  class="bg-sky-100 text-neutral-600"  size="xs">
                <MobilePhoneSolid class="w-4 mr-2"/>
                Zum Telefoninterview einladen.
            </Button>
            <Tooltip>Bewerber auf "In Bearbeitung" setzen und Einladungs-Mail senden</Tooltip>
        {:else if employee.employmentStatus === 'training-session'}
            <input type="hidden" name="status" value="training-verified">
            <Button type="submit" class="bg-sky-100 text-neutral-600" size="xs">
                <ClipboardCheckSolid class="w-4 mr-2"/>
                Schulung abgeschlossen
            </Button>
        {:else if employee.employmentStatus === 'rebuddy'}
            <input type="hidden" name="status" value="active">
            <Button type="submit" class="bg-sky-100 text-neutral-600" size="xs">
                <BellActiveAltSolid class="w-4 mr-2"/>
                rebuddy-Termin abgeschlossen
            </Button>
        {/if}
    </form>
<Modal title="Move to Pool" bind:open={poolModal}>
    <p class="text-base leading-relaxed text-gray-500 dark:text-gray-400">
        Bitte Grund angeben, warum die Bewerbung in den Pool verschoben werden soll.
    </p>
    <form on:submit|preventDefault={() => moveToPool()} class="space-y-4">
        <Label class="space-y-2">
            <span>Schnellauswahl</span>
            <Select on:change={e => poolReason = e.target.value}>
                <option value="Absage nach GTK">Absage nach GTK</option>
                <option value="Kandidat tritt von Bewerbung zurück">Kandidat tritt von Bewerbung zurück</option>
                <option value="Erfüllt Einstiegskriterien nicht">Erfüllt Einstiegskriterien nicht</option>
                <option value="Doppelte Akte">Doppelte Akte</option>
            </Select>
        </Label>
        <Label class="space-y-2">
            <span>Grund</span>
            <Textarea bind:value={poolReason} required/>
        </Label>
        <div class="flex justify-between">
            <Button color="red" type="button" on:click={() => poolModal = false}>Abbrechen</Button>
            <Button type="submit">In den Pool verschieben</Button>
        </div>
    </form>
</Modal>
<Modal bind:open={previewModal} size="xl" title="Die Statusänderung beinhaltet Trigger!">

    {#if html === ''}
        <Spinner/>
    {:else}
        <div class="grid grid-cols-2 gap-3">
            <div class="mt-12">
                <Card class="min-w-full">
                    <p>
                        Die Statusänderung löst eine E-Mail an {employee.email} ({employee.name}) aus.
                    </p>
                    <p>
                        Sollte dieser Schritt etwaige Maßnahmen zur Vorbereitung benötigen, sollten diese erst erfüllt sein.
                    </p>
                    <div class="flex justify-between mt-2">
                        <Button type="button" on:click={() => submitForm()} class="bg-sky-100 text-neutral-600" size="lg">Fortfahren & Status ändern</Button>
                        <Button type="button" on:click={() => previewModal = false} color="red" size="lg">Abbrechen</Button>
                    </div>
                </Card>

            </div>
            <div>
                <h3 class="text-2xl my-3 text-neutral-600">Vorschau</h3>
                <Select bind:value={templateId} items={templates.map(template => ({name: template.Name, value: template.ID}))} on:change={previewEmail} required/>
                <Label class="space-y-2 my-3">
                    <span>Subject</span>
                    <Input required bind:value={subject} type="text"/>
                </Label>
                <div>{@html html}</div>
            </div>

        </div>

    {/if}
</Modal>
